import React, { useRef, useState, useEffect, useMemo, useId } from "react";
import "./ComboBox.css";

const SelectActions = {
  Close: 0,
  CloseSelect: 1,
  First: 2,
  Last: 3,
  Next: 4,
  Open: 5,
  PageDown: 6,
  PageUp: 7,
  Previous: 8,
  Select: 9,
  Type: 10,
};

function getActionFromKey(event, menuOpen) {
  const { key, altKey, ctrlKey, metaKey } = event;
  const openKeys = ["ArrowDown", "ArrowUp", "Enter", " "];

  if (!menuOpen && openKeys.includes(key)) return SelectActions.Open;
  if (key === "Home") return SelectActions.First;
  if (key === "End") return SelectActions.Last;

  if (
    key === "Backspace" ||
    key === "Clear" ||
    (key.length === 1 && key !== " " && !altKey && !ctrlKey && !metaKey)
  ) {
    return SelectActions.Type;
  }

  if (menuOpen) {
    if (key === "ArrowUp" && altKey) return SelectActions.CloseSelect;
    else if (key === "ArrowDown" && !altKey) return SelectActions.Next;
    else if (key === "ArrowUp") return SelectActions.Previous;
    else if (key === "PageUp") return SelectActions.PageUp;
    else if (key === "PageDown") return SelectActions.PageDown;
    else if (key === "Escape") return SelectActions.Close;
    else if (key === "Enter" || key === " ") return SelectActions.CloseSelect;
  }
}

function getUpdatedIndex(currentIndex, maxIndex, action) {
  const pageSize = 10;
  switch (action) {
    case SelectActions.First:
      return 0;
    case SelectActions.Last:
      return maxIndex;
    case SelectActions.Previous:
      return Math.max(0, currentIndex - 1);
    case SelectActions.Next:
      return Math.min(maxIndex, currentIndex + 1);
    case SelectActions.PageUp:
      return Math.max(0, currentIndex - pageSize);
    case SelectActions.PageDown:
      return Math.min(maxIndex, currentIndex + pageSize);
    default:
      return currentIndex;
  }
}

const ComboBox = ({
  label,
  options = [],
  value,
  onChange,
  classes,
  ariaLabel,
  disabled = false,
  handleFocusRef = (ref) => null,
  extraProps = {},
}) => {
  const uniqueId = useId();
  const comboId = `combo-${uniqueId}`;
  const mouseDownInsideRef = useRef(false);

  const internalOptions = useMemo(() => options, [options]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [searchString, setSearchString] = useState("");
  const searchTimeout = useRef(null);
  const comboRef = useRef(null);
  const listboxRef = useRef(null);

  handleFocusRef(comboRef);

  useEffect(() => {
    if (value !== undefined) {
      const idx = internalOptions.indexOf(value);
      setSelectedIndex(idx);
      setActiveIndex(idx >= 0 ? idx : 0);
    }
  }, [value, internalOptions]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        comboRef.current &&
        !comboRef.current.contains(event.target) &&
        listboxRef.current &&
        !listboxRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateSearch = (char) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    const newSearch = searchString + char;
    setSearchString(newSearch);
    searchTimeout.current = setTimeout(() => setSearchString(""), 500);
    return newSearch;
  };

  const onOptionChange = (index) => {
    const max = internalOptions.length - 1;
    const clamped = Math.max(0, Math.min(max, index));
    setActiveIndex(clamped);
    const activeId = `${comboId}-option-${clamped}`;
    comboRef.current.setAttribute("aria-activedescendant", activeId);

    if (open) {
      const optionEl = document.getElementById(activeId);
      if (optionEl && listboxRef.current) {
        optionEl.scrollIntoView({ block: "nearest" });
      }
    }
  };

  const selectOption = (index) => {
    setSelectedIndex(index);
    setActiveIndex(index);
    onChange?.(internalOptions[index]);
  };

  const onKeyDown = (event) => {
    const action = getActionFromKey(event, open);
    if (action === undefined) return;

    const max = internalOptions.length - 1;

    switch (action) {
      case SelectActions.First:
      case SelectActions.Last:
      case SelectActions.Next:
      case SelectActions.Previous:
      case SelectActions.PageUp:
      case SelectActions.PageDown: {
        event.preventDefault();
        if (!open) setOpen(true);
        onOptionChange(getUpdatedIndex(activeIndex, max, action));
        return;
      }
      case SelectActions.CloseSelect:
        event.preventDefault();
        selectOption(activeIndex);
        setOpen(false);
        return;
      case SelectActions.Close:
        event.preventDefault();
        setOpen(false);
        return;
      case SelectActions.Type:
        const search = updateSearch(event.key);
        const matchIndex = internalOptions.findIndex((opt) =>
          String(opt).toLowerCase().startsWith(search.toLowerCase())
        );
        if (matchIndex >= 0) onOptionChange(matchIndex);
        return;
      case SelectActions.Open:
        event.preventDefault();
        setOpen(true);
        onOptionChange(activeIndex >= 0 ? activeIndex : 0);
        return;
      default:
        return;
    }
  };

  const onKeyUp = (e) => {
    if (e.key === "Tab") setOpen(false);
  };

  return (
    <div
      className={`combo ${open ? "open" : ""} ${classes ? classes : ""} ${
        disabled ? "combo-disabled" : ""
      }`}
    >
      <div
        id={comboId}
        className={`combo-input ${
          selectedIndex === -1 ? "placeholderClass" : ""
        }`}
        role="combobox"
        aria-controls={`${comboId}-listbox`}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        // aria-labelledby={`${comboId}-label`}
        aria-activedescendant={
          open && activeIndex >= 0
            ? `${comboId}-option-${activeIndex}`
            : ""
        }
        tabIndex={disabled ? -1 : 0}
        ref={comboRef}
        onClick={() => {
          if (!disabled) setOpen((o) => !o);
        }}
        onKeyDown={(e) => {
          if (!disabled) onKeyDown(e);
        }}
        onKeyUp={(e) => {
          if (!disabled) onKeyUp(e);
        }}
        onBlur={(e) => {
          if (disabled) return;
          if (mouseDownInsideRef.current) return;
          if (!e.currentTarget.contains(e.relatedTarget)) {
            setOpen(false);
          }
        }}
      >
        {selectedIndex === -1 ? (
          label
        ) : (
          <>
            {internalOptions[selectedIndex]}
            {extraProps[internalOptions[selectedIndex]]}
          </>
        )}
      </div>

      {!disabled && open && (
        <div
          className="combo-menu"
          role="listbox"
          id={`${comboId}-listbox`}
          aria-labelledby={`${comboId}-label`}
          tabIndex={-1}
          ref={listboxRef}
          onMouseDown={() => {
            mouseDownInsideRef.current = true;
          }}
          onMouseUp={() => {
            setTimeout(() => (mouseDownInsideRef.current = false), 0);
          }}
        >
          {internalOptions.map((opt, idx) => (
            <div
              key={idx}
              id={`${comboId}-option-${idx}`}
              role="option"
              className={`combo-option ${
                idx === activeIndex ? "option-current" : ""
              }`}
              aria-selected={idx === selectedIndex}
              aria-label={`${opt}${idx === selectedIndex ? " selected" : ""}`}
              onMouseEnter={() => onOptionChange(idx)}
              onClick={() => {
                selectOption(idx);
                setOpen(false);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                selectOption(idx);
                setOpen(false);
              }}
            >
              {opt}
              {extraProps[opt]}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ComboBox;
